import { __extends } from "tslib";
import { addClass, closestElement, emitEvent, getShadowElement, notChildEventListener, positionElement, removeClass, removeElement } from '@tyler-components-web/core';
import { deepQuerySelectorAll, getActiveElement } from 'shadow-utils';
import { BaseAdapter } from '../core/adapters/base-adapter';
import { setPopupPosition } from '../utils/positioning-utils';
import { POPUP_CONSTANTS as constants, POPUP_CONSTANTS } from './popup-constants';
var PopupAdapter = /** @class */ (function (_super) {
    __extends(PopupAdapter, _super);
    function PopupAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._positionAdjustment = {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        };
        _this._rootElement = getShadowElement(component, POPUP_CONSTANTS.selectors.CONTAINER);
        return _this;
    }
    PopupAdapter.prototype.setAttribute = function (attribute, value, element) {
        if (element) {
            element.setAttribute(attribute, value);
        }
        else {
            this._component.setAttribute(attribute, value);
        }
    };
    PopupAdapter.prototype.removeAttribute = function (attribute, element) {
        if (element) {
            element.removeAttribute(attribute);
        }
        else {
            this._component.removeAttribute(attribute);
        }
    };
    PopupAdapter.prototype.positionPopup = function () {
        var position = positionElement(this._component, this._component.targetElement, "auto " + this._component.placement, this._hostElement);
        position.top += this._positionAdjustment.top;
        position.bottom += this._positionAdjustment.bottom;
        position.left += this._positionAdjustment.left;
        position.right += this._positionAdjustment.right;
        setPopupPosition(this._component, position);
        emitEvent(this._component, constants.events.POSITION, position);
    };
    PopupAdapter.prototype.addPopup = function (targetElement, manageFocus) {
        var _this = this;
        this._component.setAttribute('tabindex', '-1');
        this._component.setAttribute(POPUP_CONSTANTS.attributes.HOST, '');
        // Set initial position to top-left of host element, and translate
        // far off screen while we wait for positioning. This ensures that
        // the popup is not visible, nor does it affect layouts before it
        // is properly moved into its expected location.
        var targetBounds = targetElement.getBoundingClientRect();
        var targetHeight = targetBounds.height >= 0 ? targetBounds.height : 0;
        var startTop = targetBounds.top >= 0 ? targetBounds.top + targetHeight : 0;
        var startLeft = targetBounds.left >= 0 ? targetBounds.left : 0;
        this._component.style.top = startTop + "px";
        this._component.style.left = startLeft + "px";
        this._component.style.transform = 'translateX(-9999px) translateY(-9999px)';
        var hostDocument = targetElement.ownerDocument || document;
        this._hostElement = closestElement(POPUP_CONSTANTS.selectors.HOST, targetElement) || hostDocument.body;
        this._hostElement.appendChild(this._component);
        window.requestAnimationFrame(function () { return _this.positionPopup(); });
        if (manageFocus) {
            this._previouslyFocusedElement = getActiveElement();
            this._component.focus();
        }
    };
    PopupAdapter.prototype.removePopup = function (manageFocus) {
        var _this = this;
        removeElement(this._component);
        if (manageFocus) {
            window.requestAnimationFrame(function () {
                if (_this._previouslyFocusedElement) {
                    var activeElement = getActiveElement();
                    if (!activeElement || activeElement === document.body) {
                        _this._previouslyFocusedElement.focus();
                    }
                    _this._previouslyFocusedElement = undefined;
                }
            });
        }
        this._hostElement = undefined;
    };
    PopupAdapter.prototype.manageWindowEvents = function (add) {
        var _this = this;
        if (add === void 0) { add = false; }
        if (add) {
            this._windowEventCallback = function (evt) { return _this._windowInteraction(evt); };
            window.addEventListener('scroll', this._windowEventCallback, true);
            window.addEventListener('resize', this._windowEventCallback, true);
        }
        else {
            if (this._windowEventCallback) {
                window.removeEventListener('scroll', this._windowEventCallback, true);
                window.removeEventListener('resize', this._windowEventCallback, true);
                this._windowEventCallback = undefined;
            }
        }
    };
    PopupAdapter.prototype.dispatchEvent = function (type, data, bubbles, cancelable) {
        if (this._component.targetElement) {
            return !emitEvent(this._component.targetElement, type, data, bubbles, cancelable);
        }
        return false;
    };
    PopupAdapter.prototype.addClass = function (classes) {
        addClass(classes, this._rootElement);
    };
    PopupAdapter.prototype.removeClass = function (classes) {
        removeClass(classes, this._rootElement);
    };
    PopupAdapter.prototype.setAnimationEndListener = function (listener, classes) {
        var _this = this;
        window.requestAnimationFrame(function () {
            if (classes && classes.length) {
                addClass(classes, _this._rootElement);
            }
            _this._rootElement.addEventListener('transitionend', listener);
        });
    };
    PopupAdapter.prototype.removeEventListener = function (type, listener) {
        this._rootElement.removeEventListener(type, listener);
    };
    PopupAdapter.prototype.setBlurListener = function (listener) {
        var _this = this;
        return notChildEventListener(this._component, function (activeElement) {
            if (!_this._component.contains(activeElement)) {
                listener();
            }
        }, true);
    };
    PopupAdapter.prototype.trySetInitialFocus = function () {
        var _this = this;
        window.requestAnimationFrame(function () {
            var elements = deepQuerySelectorAll(_this._component, POPUP_CONSTANTS.selectors.INITIAL_FOCUS);
            if (elements && elements.length) {
                var initialElement = elements[elements.length - 1];
                initialElement.focus();
            }
        });
    };
    PopupAdapter.prototype._windowInteraction = function (evt) {
        if (!this._component.open) {
            return;
        }
        switch (evt.type) {
            case 'scroll':
            case 'resize':
                this.positionPopup();
                break;
            default:
                break;
        }
    };
    Object.defineProperty(PopupAdapter.prototype, "positionAdjustment", {
        get: function () {
            return this._positionAdjustment;
        },
        set: function (value) {
            if (!value) {
                this._positionAdjustment = {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0
                };
            }
            else {
                var top_1 = value.top, bottom = value.bottom, left = value.left, right = value.right;
                this._positionAdjustment = {
                    top: top_1 || 0,
                    bottom: bottom || 0,
                    left: left || 0,
                    right: right || 0
                };
            }
            this.positionPopup();
        },
        enumerable: true,
        configurable: true
    });
    return PopupAdapter;
}(BaseAdapter));
export { PopupAdapter };
